import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, FormHelperText, InputLabel, OutlinedInput, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { Dayjs } from 'dayjs';
import { shouldSyncDate, toApiDate, toDayjs } from '../utils/date';

// Rodapé das telas de cadastro em página inteira: Cancelar volta, Salvar bloqueia enquanto salva
export function FormPageFooter({ onCancel, onSave, isSaving, saveLabel = 'Salvar', cancelLabel = 'Cancelar', disabled }: {
    onCancel: () => void; onSave: () => void; isSaving: boolean; saveLabel?: string; cancelLabel?: string; disabled?: boolean;
}) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, mt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={onCancel} disabled={isSaving}>
                {cancelLabel}
            </Button>
            <LoadingButton variant="contained" startIcon={<SaveIcon />} loadingPosition="start" onClick={onSave} loading={isSaving} disabled={isSaving || disabled}>
                {saveLabel}
            </LoadingButton>
        </Box>
    );
}

// Título de seção dentro de formulários em página inteira
export function FormSectionTitle({ title, description, first }: { title: string; description?: string; first?: boolean }) {
    return (
        <React.Fragment>
            <Typography variant="h6" sx={{ mb: description ? 0.5 : 2, mt: first ? 0 : 2, color: 'primary.main', fontWeight: 600 }}>
                {title}
            </Typography>
            {description && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>}
            <Divider sx={{ mb: 2 }} />
        </React.Fragment>
    );
}

export function FieldError({ message }: { message?: string | false }) {
    return message ? <FormHelperText error>{message}</FormHelperText> : null;
}

// DatePicker com rótulo acima, valor dayjs e retorno "YYYY-MM-DD" (ou null quando limpo)
export function DateField({ label, value, onChange, required, error, disabled, minDate, maxDate }: {
    label: string;
    value?: string | null;
    onChange: (value: string | null) => void;
    required?: boolean;
    error?: string | false;
    disabled?: boolean;
    minDate?: Dayjs;
    maxDate?: Dayjs;
}) {
    const [inner, setInner] = useState<Dayjs | null>(toDayjs(value));

    useEffect(() => {
        if (shouldSyncDate(inner, value)) {
            setInner(toDayjs(value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
        <React.Fragment>
            <InputLabel required={required}>{label}</InputLabel>
            <DesktopDatePicker
                inputFormat="DD/MM/YYYY"
                value={inner}
                disabled={disabled}
                minDate={minDate}
                maxDate={maxDate}
                onChange={(newValue: Dayjs | null) => {
                    setInner(newValue);
                    onChange(toApiDate(newValue));
                }}
                renderInput={({ inputRef, inputProps, InputProps }) => (
                    <OutlinedInput
                        fullWidth
                        inputRef={inputRef}
                        inputProps={{ ...inputProps, autoComplete: 'off' }}
                        endAdornment={InputProps?.endAdornment}
                        error={!!error}
                        disabled={disabled}
                    />
                )}
            />
            <FieldError message={error} />
        </React.Fragment>
    );
}
